import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col } from "react-bootstrap";
import {
  decodeJwtAction,
  getConfigDefaultAction,
  getConfigsAction,
} from "store/actions/actionCreators";
import MainNav from "components/nav/MainNav";
import DashboardSidebar from "components/sidebar/DashboardSidebar";
import ConfigKeyValueTable from "components/dashboard/config/ConfigKeyValueTable";
import {
  getConfigsReducerResponse,
  getConfigDefaultReducerResponse,
  getJwtReducerResponse,
} from "store/selectors";
import { getTheme } from "lib/theme";
import { Config, ConfigDefault, JWT } from "types";
import { administratorRoleId } from "settings";
import * as routes from "routes";
import {
  Breadcrumb,
  BreadcrumbData,
  InfoCard,
  InfoTableCard,
  Page,
} from "@avlino-inc/alenza-react";
import { faKey } from "@fortawesome/pro-light-svg-icons/faKey";
import { faClock } from "@fortawesome/pro-light-svg-icons/faClock";
import { faMobile } from "@fortawesome/pro-light-svg-icons/faMobile";

const DashboardConfigPage = ({ authed }: { authed: Promise<boolean> }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const configData: Config[] = useSelector((state) =>
    getConfigsReducerResponse(state, "data")
  );
  const configDefaultData: ConfigDefault = useSelector((state) =>
    getConfigDefaultReducerResponse(state, "data")
  );
  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );

  const isAdmin = (jwtPayload: JWT, validRoleId: number) => {
    return jwtPayload && jwtPayload.role_id === validRoleId;
  };

  const isAvlino = (jwtPayload: JWT) => {
	return jwtPayload?.email?.endsWith('@avlino.com')
  }

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("DashboardConfigPage useEffect()...");
    const theme: string = getTheme();
    dispatch(decodeJwtAction({}));
    dispatch(getConfigDefaultAction({ theme }));
    dispatch(getConfigsAction({}));
  }, [dispatch]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  const twoFactorOverrideKey: string | undefined =
    configDefaultData && configDefaultData.app.twoFactorEnabledOverrideKey;
  const twoFactorOverrideStatus: string | undefined =
    configData &&
    configData.find((conf) => conf.key === twoFactorOverrideKey)?.value;
  const twoFactorDefaultStatus: boolean | undefined =
    configDefaultData && configDefaultData.app.twoFactorEnabled;

  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.HOME,
        label: "Command Center",
      },
      {
        url: routes.DASHBOARD_ADMIN,
        label: "Admin Dashboard",
      },
      {
        url: routes.DASHBOARD_CONFIGURATION,
        label: "Configuration",
      },
    ],
    []
  );

  const lastModified = useMemo(() => {
    if (!configData) return;
    let sortedConfig = configData.sort((a: Config, b: Config) => a.id - b.id);
    let lastModified = sortedConfig[sortedConfig.length - 1];
    return lastModified;
  }, [configData]);

  const twoFactorValue = useMemo(() => {
    if (twoFactorOverrideStatus) {
      // Given as a string
      if (twoFactorOverrideStatus == "true") {
        return "ON";
      } else {
        return "OFF";
      }
    } else {
      // Given as a boolean
      return twoFactorDefaultStatus ? "ON" : "OFF";
    }
  }, [twoFactorDefaultStatus, twoFactorOverrideStatus]);
  const [showCreateKeyValueModal, setShowCreateKeyValueModal] = useState(false);
  const [showSearch, setShowSearch] = useState(true);

  return (
    <div id="DashboardConfigPage">
      <MainNav
        first_name={jwtPayloadData && jwtPayloadData.first_name}
        last_name={jwtPayloadData && jwtPayloadData.last_name}
        username={jwtPayloadData && jwtPayloadData.username}
        email={jwtPayloadData && jwtPayloadData.email}
      />
      <div style={{ display: "flex" }}>
        <DashboardSidebar
          isAvlino={isAvlino(jwtPayloadData)}
          isAdmin={isAdmin(jwtPayloadData, administratorRoleId)}
        />
        <main id="page-wrap">
          <Container fluid={true} style={{ marginBottom: "100px" }}>
            <Page.Header>
              <Breadcrumb items={breadcrumbItems} onClickItem={history.push} />
            </Page.Header>
            <Row>
              <Col xs={12} xl={4}>
                <InfoCard
                  label="Total Keys"
                  value={configData?.length || 0}
                  color="bright-blue"
                  icon={faKey}
                />
                <br />
              </Col>
              <Col xs={12} xl={4}>
                <InfoCard
                  value={lastModified?.key_id}
                  label={`Last Modified Key ID`}
                  color="ketchup"
                  icon={faClock}
                />
                <br />
              </Col>
              <Col xs={12} xl={4}>
                <div title="Override with 'admin.twofactor.enabled' key">
                  <InfoCard
                    value={twoFactorValue}
                    label={`Two-Factor`}
                    color="eggplant"
                    icon={faMobile}
                  />
                </div>
                <br />
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={12} lg={12} xl={12}>
                <InfoTableCard
                  icon={faKey}
                  color="bright-blue"
                  onNewClick={() => {
                    setShowCreateKeyValueModal(!showCreateKeyValueModal);
                  }}
                  onMenuClick={() =>
                    showSearch ? setShowSearch(false) : setShowSearch(true)
                  }
                >
                  <ConfigKeyValueTable
                    showCreateModal={showCreateKeyValueModal}
                    setShowCreateModal={setShowCreateKeyValueModal}
                    config={configData}
                    configDefault={configDefaultData}
                    showSearch={showSearch}
                  />
                </InfoTableCard>
              </Col>
            </Row>
          </Container>
        </main>
      </div>
    </div>
  );
};

export default DashboardConfigPage;
